import { useEffect, useMemo, useState } from 'react'
import { collection, onSnapshot, orderBy, query, where, type DocumentData } from 'firebase/firestore'
import { ensureViewerRealtime, type RealtimeViewer } from '../lib/realtimeAuth'
import { getGuestId } from '../lib/guestId'
import { api } from '../services/api'
import { useStore, type Order, type CartItem } from '../store/useStore'

/**
 * #1641 同じ卓の問い合わせを画面内で 1 本にまとめる。
 *
 * `useMyOrders` は注文状況・卓のバナー・提供通知など**複数の部品から同時に使われる**。
 * 直読の頃は購読が共有されていたので無害だったが、API に寄せた今は同じ卓の取得が
 * 部品の数だけ飛ぶ。進行中の取得を使い回し、直近の結果は数秒だけ再利用する。
 */
const TABLE_ORDERS_TTL_MS = 8_000
type TableOrdersResult = Awaited<ReturnType<typeof api.listTableOrders>>
const tableOrdersCache = new Map<string, { at: number; promise: Promise<TableOrdersResult> }>()

function fetchTableOrders(
  storeId: string,
  tableNumber: string,
  viewerKey: string,
): Promise<TableOrdersResult> {
  // #1641 応答は**呼び出した人から見た結果** (自分の注文はサーバが除く) なので、
  // 見る人までキーに含める。ゲスト → ログインの切り替え直後に、引き継いだ自分の
  // 注文を「友達の分」として二重に数えないため
  const key = `${viewerKey}|${storeId}|${tableNumber}`
  const hit = tableOrdersCache.get(key)
  if (hit && Date.now() - hit.at < TABLE_ORDERS_TTL_MS) return hit.promise
  const promise = api.listTableOrders(storeId, tableNumber)
  // 失敗はキャッシュに残さない (次の周期で取り直せるように)
  promise.catch(() => tableOrdersCache.delete(key))
  tableOrdersCache.set(key, { at: Date.now(), promise })
  return promise
}

const ACTIVE_STATUSES: Order['status'][] = ['received', 'preparing', 'served', 'out_for_delivery']
const HISTORY_STATUSES: Order['status'][] = ['cancelled', 'delivered']

/** Firestore の Timestamp も API の ISO 文字列も同じ形にする (#1641 の API 取得で両方来る)。 */
function toIso(value: unknown): string | undefined {
  const ts = value as { toDate?: () => Date } | string | undefined
  if (typeof ts === 'string') return ts
  return ts?.toDate?.()?.toISOString?.()
}

function mapOrderDoc(id: string, data: DocumentData): Order {
  return {
    id,
    storeId: data.storeId,
    // 注文時の店名の控え。一覧に出ない店 (unlisted) でも店名を出すため
    storeName: data.storeName || undefined,
    customerId: data.customerId,
    customerName: data.customerName,
    // #977 注文者の識別（店員代理入力 = 'staff'）
    placedBy: data.placedBy === 'staff' ? 'staff' : 'customer',
    items: (data.items || []) as CartItem[],
    totalAmount: data.totalAmount ?? 0,
    status: data.status,
    paid: !!data.paid,
    // #1214 呼び出し番号 (A-01)。厨房伝票・レシート・客画面で同じ番号を出す。
    callNumber: data.callNumber,
    // #1216 受け取り案内を表示済みか (サーバ側既読フラグ)。端末を跨いで一度きり。
    readyNoticeShownAt: toIso(data.readyNoticeShownAt),
    // #1216 時間窓の起点 — `ready` になった時刻 (無ければ updatedAt/createdAt)
    readyAt: toIso(data.readyAt) ?? toIso(data.updatedAt),
    // #941/#942 現金会計
    paymentMethod: data.paymentMethod,
    cashPending: !!data.cashPending,
    tableNumber: data.tableNumber,
    notes: data.notes,
    paymentIntentId: data.paymentIntentId,
    paidAt: toIso(data.paidAt),
    missingReported: !!data.missingReported,
    remadeFromOrderId: data.remadeFromOrderId,
    // #284 delivery fields — same explicit mapping as useStoreOrders.
    orderType: data.orderType,
    deliveryAddress: data.deliveryAddress,
    deliveryNotes: data.deliveryNotes,
    deliveryLat: typeof data.deliveryLat === 'number' ? data.deliveryLat : undefined,
    deliveryLng: typeof data.deliveryLng === 'number' ? data.deliveryLng : undefined,
    captured: typeof data.captured === 'boolean' ? data.captured : undefined,
    cancelledBy: data.cancelledBy,
    cancelReason: data.cancelReason,
    unreadCountForCustomer: typeof data.unreadCountForCustomer === 'number' ? data.unreadCountForCustomer : 0,
    unreadCountForOwner: typeof data.unreadCountForOwner === 'number' ? data.unreadCountForOwner : 0,
    // Timestamp 型が正だが、外部投入データ等の ISO 文字列も表示に耐えるようにする
    // (#1535 で注文時刻を客画面に出すようになったため、黙って '' に落とさない)
    createdAt: data.createdAt?.toDate?.()?.toISOString?.()
      ?? (typeof data.createdAt === 'string' ? data.createdAt : ''),
    updatedAt: data.updatedAt?.toDate?.()?.toISOString?.()
      ?? (typeof data.updatedAt === 'string' ? data.updatedAt : ''),
  }
}

/**
 * Realtime subscription to the current user's orders (#289).
 * Instantly reflects owner status changes (received → preparing → served)
 * so the customer can track their order without reloading.
 *
 * #977: also pulls in staff-input orders (placedBy='staff') at the customer's
 * current dine-in table so the customer's bill is complete and the table-wide
 * checkout doesn't silently miss them. Staff orders use a synthetic customerId
 * (`staff_{storeId}`) so they aren't in the customerId query above.
 */
export function useMyOrders() {
  const { user } = useStore()
  // #1398 ゲスト注文は端末のゲストIDが customerId。ログイン中はアカウントを優先する
  // (引き継ぎ済みならゲストIDの注文はアカウント側に移っている)。
  const guestId = user?.id ? null : getGuestId()
  const [customerOrders, setCustomerOrders] = useState<Order[]>([])
  // #977 同じテーブルの「他者」の未払い注文（友達＝他アプリ客 ＋ 店員入力）。
  const [tableOrders, setTableOrders] = useState<Order[]>([])
  // ログイン客もゲストも「自分の注文」の引き当ては customerId 一致で同じ。
  const customerKey = user?.id || guestId || null
  const [loading, setLoading] = useState(!!customerKey)
  // #1641 注文の直読は「本人だけ」に閉じたので、購読の前に customerId クレームを用意する。
  // ログイン中は既定のアプリ、ゲストは購読専用アプリ (匿名) — 既定のアプリを匿名で
  // 埋めると、API 呼び出しがメール無しのトークンになって 401 になるため。
  // #1922 **誰として読むかはサーバに決めさせる** (書き込みと同じ順番)。端末の
  // ゲスト ID で絞ると、Firebase にログイン済みの端末で出した注文が出なくなる。
  const [viewer, setViewer] = useState<RealtimeViewer | null>(null)
  // #1641 購読が使えないとき (権限の用意に失敗 / ルールで拒否) は API 取得へ倒す
  const [fallback, setFallback] = useState(false)

  useEffect(() => {
    if (!customerKey) return
    let cancelled = false
    const ready = ensureViewerRealtime()
    ready
      .then((resolved) => {
        if (cancelled) return
        setViewer(resolved)
        // 用意できなければ購読はあきらめて API 取得へ (画面を空にしない)
        if (!resolved) setFallback(true)
      })
      .catch((e: unknown) => {
        console.error('[useMyOrders] realtime auth failed:', e)
        if (cancelled) return
        setViewer(null)
        setFallback(true)
      })
    return () => {
      cancelled = true
    }
  }, [customerKey, user?.id])

  const [trackedUserId, setTrackedUserId] = useState(customerKey)
  if (customerKey !== trackedUserId) {
    setTrackedUserId(customerKey)
    setCustomerOrders([])
    setTableOrders([])
    setLoading(!!customerKey)
    // #1641 客が変わったら購読先も取り直す (前の人のトークンで繋がない)
    setViewer(null)
    setFallback(false)
  }

  // #1641 購読が使えないときの API 取得 (自分の注文だけ。10 秒ごと)。
  useEffect(() => {
    if (!customerKey || !fallback) return
    let cancelled = false
    const load = async () => {
      try {
        // #1849 と同じ理由で id はクエリに載せない (認証はヘッダで足りる)
        const res = await api.listMyOrders()
        if (cancelled) return
        const list = ((res as { orders?: unknown[] }).orders || []) as Record<string, unknown>[]
        setCustomerOrders(list.map((o) => mapOrderDoc(String(o.id), o)))
      } catch (e) {
        console.error('[useMyOrders] fallback fetch failed:', e)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void load()
    const timer = window.setInterval(() => void load(), 10_000)
    return () => {
      cancelled = true
      window.clearInterval(timer)
    }
  }, [customerKey, fallback])

  useEffect(() => {
    if (!customerKey || !viewer || fallback) return

    const q = query(
      collection(viewer.db, 'orders'),
      // #1922 サーバが書き込みに使う ID で絞る (端末のゲスト ID とは限らない)
      where('customerId', '==', viewer.customerId),
      orderBy('createdAt', 'desc'),
    )

    const unsubscribe = onSnapshot(
      q,
      (snap) => {
        const list: Order[] = []
        snap.forEach((d) => list.push(mapOrderDoc(d.id, d.data())))
        setCustomerOrders(list)
        setLoading(false)
      },
      (err) => {
        // Transient errors: keep the last-known list instead of clearing,
        // so a flaky network or a token refresh doesn't wipe the UI.
        console.error('[useMyOrders] subscription error:', err)
        setLoading(false)
        // #1641 拒否されたら API 取得に切り替える (注文状況・呼び出し番号を止めない)
        if ((err as { code?: string }).code === 'permission-denied') setFallback(true)
      },
    )

    return () => unsubscribe()
  }, [customerKey, viewer, fallback])

  // #977 顧客が現在いる dine-in の卓（未払い）。ここに入った店員入力注文も拾う。
  const activeTable = useMemo(() => {
    const o = customerOrders.find((x) => !x.paid && x.orderType !== 'delivery' && x.tableNumber)
    return o ? { storeId: o.storeId, tableNumber: o.tableNumber as string } : null
  }, [customerOrders])

  // #1641 同じテーブルの「他者」の未払い注文 (友達 + 店員入力)。以前は orders が
  // 公開読み取りだったので卓の全注文を直接購読していた。閉じたので、着席している
  // 人だけが取れる API から取る。自分の注文が動いたとき (= 購読が発火したとき) と、
  // 席にいる間の緩いポーリングで取り直す。
  const tableStoreId = activeTable?.storeId ?? null
  const tableNumber = activeTable?.tableNumber ?? null
  useEffect(() => {
    // 卓から離れたら取りに行かない。表示側は activeTable が無いと tablemates を出さない
    if (!tableStoreId || !tableNumber || !customerKey) return
    let cancelled = false
    const load = async () => {
      try {
        // #1922 キャッシュのキーも「サーバから見た自分」で持つ
        const res = await fetchTableOrders(tableStoreId, tableNumber, viewer?.customerId ?? customerKey)
        if (cancelled) return
        setTableOrders(res.orders.map((o) => ({
          id: o.id,
          storeId: o.storeId,
          // #1641 同席者が誰かは返さない (会計に要らない)。型を満たすための空文字
          customerId: '',
          placedBy: o.placedBy,
          items: (o.items || []) as CartItem[],
          totalAmount: o.totalAmount,
          status: o.status as Order['status'],
          paid: o.paid,
          callNumber: o.callNumber ?? undefined,
          tableNumber: o.tableNumber ?? undefined,
          orderType: (o.orderType ?? undefined) as Order['orderType'],
          createdAt: o.createdAt ?? '',
          updatedAt: o.createdAt ?? '',
        })))
      } catch (e) {
        console.error('[useMyOrders] table orders fetch failed:', e)
        if (!cancelled) setTableOrders([])
      }
    }
    void load()
    const timer = window.setInterval(() => void load(), 20_000)
    return () => {
      cancelled = true
      window.clearInterval(timer)
    }
  }, [tableStoreId, tableNumber, customerKey, viewer?.customerId, customerOrders.length])

  // orders = 自分の注文のみ（通知/履歴/「自分の分」会計の整合のため）。同卓の他者は
  // tableOrders として別返し（MyOrders の「同じテーブルの注文」表示用）。activeTable が
  // 無ければ tableOrders は表示しない（stale 回避）。
  const orders = customerOrders
  const tablemates = useMemo(() => (activeTable ? tableOrders : []), [activeTable, tableOrders])
  const active = orders.filter((o) => ACTIVE_STATUSES.includes(o.status))
  const history = orders.filter((o) => HISTORY_STATUSES.includes(o.status))

  return { orders, tableOrders: tablemates, active, history, loading }
}
